import { AudioConfigDTO } from '../ports/types';

export type AppEdition = 'web_free' | 'pc_app_paid' | 'mobile_app_paid';

export interface AudioMixerOptions {
  audioContext?: AudioContext | null;
  edition?: AppEdition;
  initialConfig?: Partial<AudioConfigDTO>;
}

/**
 * オーディオミキサー＆音量管理コンポーネント (AudioMixerComponent)
 * マスター、SE、ボイスの3階層ゲイン管理、ミュート制御、マイクロフェード、
 * および AudioContext ライフサイクル（Autoplay Policy準拠）を統括する。
 */
export class AudioMixer {
  private audioContext: AudioContext | null = null;
  private edition: AppEdition = 'pc_app_paid';

  private masterVolume: number = 1.0;
  private seVolume: number = 0.8;
  private voiceVolume: number = 1.0;
  private isMuted: boolean = false;

  private duckRatio: number = 1.0;

  // Web Audio Nodes
  private masterGainNode: GainNode | null = null;
  private seGainNode: GainNode | null = null;
  private voiceGainNode: GainNode | null = null;

  constructor(options?: AudioMixerOptions) {
    if (options?.edition) {
      this.edition = options.edition;
    }

    if (options?.initialConfig) {
      this.applyConfigValues(options.initialConfig);
    }

    if (options?.audioContext !== undefined) {
      this.audioContext = options.audioContext;
    } else {
      this.initDefaultAudioContext();
    }

    this.setupAudioGraph();
    this.applyAllGains(true);
  }

  private initDefaultAudioContext(): void {
    if (typeof window !== 'undefined') {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioCtxClass) {
        try {
          this.audioContext = new AudioCtxClass();
        } catch {
          this.audioContext = null;
        }
      }
    }
  }

  private setupAudioGraph(): void {
    if (!this.audioContext) return;

    try {
      this.masterGainNode = this.audioContext.createGain();
      this.seGainNode = this.audioContext.createGain();
      this.voiceGainNode = this.audioContext.createGain();

      // Routing: SE -> Master, Voice -> Master, Master -> Destination
      this.seGainNode.connect(this.masterGainNode);
      this.voiceGainNode.connect(this.masterGainNode);
      this.masterGainNode.connect(this.audioContext.destination);
    } catch {
      // Graceful fallback in environments with partially implemented AudioContext
      this.masterGainNode = null;
      this.seGainNode = null;
      this.voiceGainNode = null;
    }
  }

  /**
   * ブラウザ Autoplay Policy に準拠した安全な再生再開
   */
  public async ensureRunning(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (err) {
        console.warn('[AudioMixer] Failed to resume AudioContext:', err);
      }
    }
  }

  public setEdition(edition: AppEdition): void {
    this.edition = edition;
    this.applyAllGains(false);
  }

  public getEdition(): AppEdition {
    return this.edition;
  }

  private clamp(val: number): number {
    if (Number.isNaN(val)) return 0;
    return Math.max(0.0, Math.min(1.0, val));
  }

  private applyConfigValues(dto: Partial<AudioConfigDTO>): void {
    if (dto.masterVolume !== undefined) {
      this.masterVolume = this.clamp(dto.masterVolume);
    }
    if (dto.seVolume !== undefined) {
      this.seVolume = this.clamp(dto.seVolume);
    }
    if (dto.voiceVolume !== undefined) {
      this.voiceVolume = this.clamp(dto.voiceVolume);
    }
    if (dto.isMuted !== undefined) {
      this.isMuted = Boolean(dto.isMuted);
    }
  }

  /**
   * 音量設定の更新
   */
  public updateConfig(dto: Partial<AudioConfigDTO>): void {
    this.applyConfigValues(dto);
    this.applyAllGains(false);
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    this.applyAllGains(false);
    return this.isMuted;
  }

  /**
   * SEダッキング（ボイス再生中の効果音音量一時減衰）
   * @param duckRatio 減衰比率 (通常 0.4 / -8dB)
   */
  public setSEDucking(duckRatio: number): void {
    this.duckRatio = Math.max(0.0, Math.min(1.0, duckRatio));
    this.applySEGain(false);
  }

  /**
   * SEダッキング解除
   */
  public resetSEDucking(): void {
    this.duckRatio = 1.0;
    this.applySEGain(false);
  }

  /**
   * 実効マスター音量の算出 (無料版は 0.0 固定)
   */
  public getEffectiveMasterVolume(): number {
    if (this.edition === 'web_free' || this.isMuted) {
      return 0.0;
    }
    return this.masterVolume;
  }

  public getEffectiveSEVolume(): number {
    return this.getEffectiveMasterVolume() * this.seVolume * this.duckRatio;
  }

  public getEffectiveVoiceVolume(): number {
    if (this.edition === 'web_free') return 0.0;
    return this.getEffectiveMasterVolume() * this.voiceVolume;
  }

  private smoothSetGain(gainNode: GainNode | null, targetValue: number, instant: boolean = false): void {
    if (!gainNode || !this.audioContext) return;

    const currentTime = this.audioContext.currentTime ?? 0;
    const gainParam = gainNode.gain;

    if (instant || typeof gainParam.setTargetAtTime !== 'function') {
      gainParam.value = targetValue;
    } else {
      // マイクロフェード: 時間定数 tau = 15ms (0.015s) によるポップノイズ防止
      try {
        gainParam.setTargetAtTime(targetValue, currentTime, 0.015);
      } catch {
        gainParam.value = targetValue;
      }
    }
  }

  private applySEGain(instant: boolean): void {
    const target = this.edition === 'web_free' ? 0.0 : (this.seVolume * this.duckRatio);
    this.smoothSetGain(this.seGainNode, target, instant);
  }

  private applyVoiceGain(instant: boolean): void {
    const target = this.edition === 'web_free' ? 0.0 : this.voiceVolume;
    this.smoothSetGain(this.voiceGainNode, target, instant);
  }

  private applyMasterGain(instant: boolean): void {
    const target = this.getEffectiveMasterVolume();
    this.smoothSetGain(this.masterGainNode, target, instant);
  }

  public applyAllGains(instant: boolean = false): void {
    this.applyMasterGain(instant);
    this.applySEGain(instant);
    this.applyVoiceGain(instant);
  }

  public getConfig(): AudioConfigDTO {
    return {
      masterVolume: this.masterVolume,
      seVolume: this.seVolume,
      voiceVolume: this.voiceVolume,
      isMuted: this.isMuted,
    };
  }

  public getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  public getSEGainNode(): GainNode | null {
    return this.seGainNode;
  }

  public getVoiceGainNode(): GainNode | null {
    return this.voiceGainNode;
  }

  public getMasterGainNode(): GainNode | null {
    return this.masterGainNode;
  }
}
